import type { InvoiceDetailResponse, InvoicePart } from '../types/invoice';

/**
 * `matchedNexLines`, `unmatchedPartIndices`, and `localMatchedNexOverrides` all use one contiguous
 * global index: line items [0..n-1], then freight, fuel, etc. Some UI (e.g. freight-tab flags)
 * still passes negative indices `-(freightLocalIndex + 1)`. Map those to the same global index
 * the API uses so exclusion / unmatch state lines up with `matchedNexLines.partIndex`.
 */
export function globalPartIndexForMatchedNexLines(
    partIndex: number,
    invoice: InvoiceDetailResponse | undefined,
    editedFields: Partial<InvoiceDetailResponse>
): number {
    if (partIndex >= 0) return partIndex;
    const lineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const fi = -(partIndex + 1);
    const freightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
    if (fi >= 0 && fi < freightItems.length) return lineItems.length + fi;
    return partIndex;
}

export type InvoiceTabularLocation =
    | { kind: 'line'; localIndex: number }
    | { kind: 'freight'; localIndex: number }
    | { kind: 'fuel'; localIndex: number }
    | { kind: 'restocking'; localIndex: number }
    | { kind: 'discount'; localIndex: number }
    | { kind: 'ignored'; localIndex: number };

/**
 * Map unified reconciliation `partIndex` (line items, then freight, fuel, restocking, discount, ignored)
 * to tab + row. Negative indices are freight rows: `-(freightRowIndex + 1)`.
 */
export function resolveTabularLocationForGlobalPartIndex(
    partIndex: number,
    invoice: InvoiceDetailResponse | undefined,
    editedFields: Partial<InvoiceDetailResponse>
): InvoiceTabularLocation | null {
    if (partIndex < 0) {
        const freightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
        const fi = -(partIndex + 1);
        if (fi >= 0 && fi < freightItems.length) return { kind: 'freight', localIndex: fi };
        return null;
    }
    const lineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const freightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
    const fuelItems = editedFields.fuelItems ?? invoice?.fuelItems ?? [];
    const restockingItems = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
    const discountItems = editedFields.discountItems ?? invoice?.discountItems ?? [];
    const ignoredParts =
        editedFields.ignoredParts ??
        invoice?.ignoredParts ??
        (invoice as { canceledParts?: InvoicePart[] } | undefined)?.canceledParts ??
        [];
    let o = 0;
    if (partIndex < o + lineItems.length) return { kind: 'line', localIndex: partIndex - o };
    o += lineItems.length;
    if (partIndex < o + freightItems.length) return { kind: 'freight', localIndex: partIndex - o };
    o += freightItems.length;
    if (partIndex < o + fuelItems.length) return { kind: 'fuel', localIndex: partIndex - o };
    o += fuelItems.length;
    if (partIndex < o + restockingItems.length) return { kind: 'restocking', localIndex: partIndex - o };
    o += restockingItems.length;
    if (partIndex < o + discountItems.length) return { kind: 'discount', localIndex: partIndex - o };
    o += discountItems.length;
    if (partIndex < o + ignoredParts.length) return { kind: 'ignored', localIndex: partIndex - o };
    return null;
}

export function getInvoicePartByGlobalPartIndex(
    partIndex: number,
    invoice: InvoiceDetailResponse | undefined,
    editedFields: Partial<InvoiceDetailResponse>
): InvoicePart | undefined {
    const loc = resolveTabularLocationForGlobalPartIndex(partIndex, invoice, editedFields);
    if (!loc) return undefined;
    const lineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const freightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
    const fuelItems = editedFields.fuelItems ?? invoice?.fuelItems ?? [];
    const restockingItems = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
    const discountItems = editedFields.discountItems ?? invoice?.discountItems ?? [];
    const ignoredParts =
        editedFields.ignoredParts ??
        invoice?.ignoredParts ??
        (invoice as { canceledParts?: InvoicePart[] } | undefined)?.canceledParts ??
        [];
    switch (loc.kind) {
        case 'line':
            return lineItems[loc.localIndex];
        case 'freight':
            return freightItems[loc.localIndex];
        case 'fuel':
            return fuelItems[loc.localIndex];
        case 'restocking':
            return restockingItems[loc.localIndex];
        case 'discount':
            return discountItems[loc.localIndex];
        case 'ignored':
            return ignoredParts[loc.localIndex];
        default:
            return undefined;
    }
}
